import { t } from "i18next";
import { CorePropsInterfaces, DTOs } from "src/core/Models";
import GtDialog from "./GtDialog";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import { Button } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { isAfter, startOfDay } from "date-fns";
import * as Constants from "../../core/Constants";
import { useState } from "react";
import { isValidDate, parseDateOnly } from "src/utils";

const dateDisplayOptions: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "long",
  day: "numeric",
};

function GtInactivateEngagementDialog({
  engagementLettersSummaryItem,
  onConfirm,
  onDialogClose,
}: CorePropsInterfaces.GtInactivateEngagementDialogProps): JSX.Element {
  const hasPlannedInactivation =
    !!engagementLettersSummaryItem.InactivationDate;
  const [inactivationDate, setInactivationDate] = useState<Date | null>(
    parseDateOnly(engagementLettersSummaryItem.InactivationDate) ??
      startOfDay(new Date())
  );

  const isDateValid = !!inactivationDate && isValidDate(inactivationDate);
  const isImmediate =
    isDateValid &&
    !isAfter(startOfDay(inactivationDate), startOfDay(new Date()));

  const inactivateEngagementDialogDTO: DTOs.GtDialogDTO = {
    data: {
      maxWidth: "sm",
      leftButtons: [],
      rightButtons: [
        <Button
          onClick={onDialogClose}
          color="tertiary"
          variant="outlined"
          key="cancelBtn"
        >
          {t("General.Cancel")}
        </Button>,
        <Button
          color="tertiary"
          variant="contained"
          key="confirmBtn"
          disabled={!isDateValid}
          onClick={() => onConfirm(inactivationDate)}
        >
          {hasPlannedInactivation
            ? t("General.Save")
            : t("General.DeactivateLetterStatus")}
        </Button>,
      ],
      title: (
        <span className="gt-finalSectionDialogIcon">
          <EventBusyOutlinedIcon />
        </span>
      ),
    },
    api: {
      onClose: onDialogClose,
    },
    state: {
      isOpen: true,
      isFullWidth: true,
    },
  };

  return (
    <GtDialog gtDialogDTO={inactivateEngagementDialogDTO}>
      <article className="gt-finalSectionDialog">
        <section className="gt-finalSectionDialog__content">
          <div className="gt-finalSectionDialog__content-question">
            {t("General.InactivateEngagementTitle", {
              companyName: engagementLettersSummaryItem.CompanyName,
            })}
          </div>
          <div className="gt-finalSectionDialog__content-explanation">
            {t("General.InactivateEngagementDetails")}
          </div>
          <div className="gt-finalSectionDialog__content-explanation">
            {t("General.InactivationDate")}
            <div className="gt-finalSectionDialog__content-dateValue">
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  format={Constants.HelpfulConstants.DateFormatYearMonthAndDay}
                  value={inactivationDate}
                  onChange={(value: Date | null) => setInactivationDate(value)}
                  slotProps={{
                    textField: {
                      className:
                        "gt-finalSectionDialog__content-datePicker gt-finalSectionDialog__content-datePicker--withIcon",
                    },
                  }}
                />
              </LocalizationProvider>
            </div>
          </div>
          {isDateValid && (
            <div className="gt-finalSectionDialog__content-explanation">
              {isImmediate
                ? t("General.InactivateEngagementImmediateInfo")
                : t("General.InactivateEngagementScheduledInfo", {
                    date: inactivationDate.toLocaleDateString(
                      [],
                      dateDisplayOptions
                    ),
                  })}
            </div>
          )}
        </section>
      </article>
    </GtDialog>
  );
}

export default GtInactivateEngagementDialog;
